import React from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import i18n from 'i18next';
import kebabCase from 'lodash/kebabCase';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import Lock from '@mui/icons-material/Lock';
import MoreHoriz from '@mui/icons-material/MoreHoriz';

import { Label } from 'uikit/labels';
import { Typo } from 'uikit/content';
import { Breadcrumbs } from 'uikit/layouts';
import { Button } from 'uikit/buttons';
import { Menu } from 'uikit/menus';
import { Tooltip } from 'uikit/tooltips';

import AppHeaderActions from './appHeaderActions';

import styles from '../styles/appHeader.module.scss';

const AppHeader = ({
  appHeaderActions = [],
  breadcrumbs = [],
  className = '',
  contentClassName = '',
  hasBreadcrumbs = false,
  hasBorder = false,
  hasHeaderActions = false,
  hasHomeLink = false,
  hasKebabMenu = false,
  hasLabel = false,
  homeOnClick = () => {},
  homeRoute = '',
  homeText = '',
  isKebabMenuDisabled = false,
  isLocked = false,
  kebabMenuItems = [],
  labelIcon = null,
  labelText = '',
  lockType = null,
  rightLabel = null,
  title = '',
}) => (
  <header className={classnames(className, hasBorder && styles.hasBorder)}>
    {hasLabel && <Label icon={labelIcon} text={labelText} type="brand" variant="s01" />}
    {(hasBreadcrumbs || hasKebabMenu) && (
      <div className={styles.breadcrumbsMenuWrap}>
        {hasBreadcrumbs && <Breadcrumbs breadcrumbs={breadcrumbs} />}
        {hasKebabMenu && (
          <Menu
            shouldAnimate={false}
            component={
              <Button
                isDisabled={isKebabMenuDisabled}
                size="xsmall"
                variant="ghost"
                icon={<MoreHoriz />}
              />
            }
          >
            {kebabMenuItems}
          </Menu>
        )}
      </div>
    )}
    <Link
      className={classnames(styles.link, { [styles.shouldShowLink]: hasHomeLink })}
      onClick={homeOnClick}
      to={homeRoute}
    >
      <KeyboardArrowLeft sx={{ height: 14, width: 14 }} />
      <Typo element="span" variant="s01">
        {homeText || i18n.t('home')}
      </Typo>
    </Link>
    <div className={classnames(styles.content, contentClassName)}>
      <div className={styles.title}>
        {isLocked && (
          <Tooltip content={i18n.t('locked-tooltip', { status: i18n.t(kebabCase(lockType)) })}>
            <Lock />
          </Tooltip>
        )}
        <Typo element="h1" variant="d04">
          {title}
        </Typo>
        {rightLabel}
      </div>
      {hasHeaderActions && <AppHeaderActions appHeaderActions={appHeaderActions} />}
    </div>
  </header>
);

AppHeader.propTypes = {
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      path: PropTypes.string,
      key: PropTypes.string,
    }),
  ),
  className: PropTypes.string,
  contentClassName: PropTypes.string,
  hasBreadcrumbs: PropTypes.bool,
  hasBorder: PropTypes.bool,
  hasHeaderActions: PropTypes.bool,
  hasHomeLink: PropTypes.bool,
  hasKebabMenu: PropTypes.bool,
  hasLabel: PropTypes.bool,
  homeOnClick: PropTypes.func,
  homeRoute: PropTypes.string,
  homeText: PropTypes.string,
  isKebabMenuDisabled: PropTypes.bool,
  isLocked: PropTypes.bool,
  kebabMenuItems: PropTypes.node,
  labelIcon: PropTypes.node,
  labelText: PropTypes.string,
  lockType: PropTypes.string,
  rightLabel: PropTypes.node,
  title: PropTypes.string,
  appHeaderActions: PropTypes.arrayOf(PropTypes.shape({})),
};

export default React.memo(AppHeader);
